import { useQuery } from '@tanstack/react-query';

import { useCurrentShop } from '@/shared/hooks/useCurrentShop';
import { ORDER_GROUPS } from '@/shared/lib/order-groups';
import { api } from '@/shared/services/api/endpoints';
import { queryKeys } from '@/shared/services/api/query-keys';

import type { BadgeKey } from '@/app/shell/navigation';
import { useAppMode, useIsSignedIn, usePermissions } from '@/stores/session.store';

/** Missions qui attendent un geste de l'agent : à planifier, puis à réaliser. */
const AGENT_ACTION_STATUSES = ['ASSIGNED', 'SCHEDULED'];
/**
 * Commandes qui attendent un geste du vendeur : un paiement à confirmer, un
 * colis à préparer, une remise à faire. Ce sont trois groupes de l'anneau du
 * tableau de bord : la pastille et l'anneau comptent la même chose.
 */
const SELLER_ACTION_STATUSES = [
  ...ORDER_GROUPS.toConfirm,
  ...ORDER_GROUPS.toPrepare,
  'DELIVERING',
].join(',');

/**
 * Les compteurs de la navigation, repris de la barre d'onglets du mobile.
 * Chaque espace ne charge que les siens ; le canal temps réel les rafraîchit
 * (il invalide ces mêmes clés), il n'y a donc aucun sondage permanent.
 */
export function useNavBadges(): Record<BadgeKey, number> {
  const signedIn = useIsSignedIn();
  const mode = useAppMode();
  const permissions = usePermissions();
  // La boutique courante, celle du tableau de bord. Un aide vendeur sans accès
  // aux commandes (« Catalogue ») ne demande pas la liste : le serveur
  // répondrait 403 à chaque chargement, pour une pastille.
  const { shopId } = useCurrentShop();
  const maySeeSellerOrders = shopId !== undefined && permissions.can('order', 'read', { shopId });

  const cart = useQuery({
    queryKey: queryKeys.cart.root,
    queryFn: api.cart.get,
    enabled: signedIn && mode === 'buyer',
  });
  const conversations = useQuery({
    queryKey: queryKeys.messaging.conversations,
    queryFn: () => api.messaging.conversations({ page: 1, pageSize: 20 }),
    enabled: signedIn && mode !== 'admin',
  });
  const sellerOrders = useQuery({
    // Le serveur compte : une page d'un élément suffit, le total est dans `meta`.
    queryKey: queryKeys.orders.list({ side: 'seller', shopId, badge: true }),
    queryFn: () =>
      api.orders.list({
        side: 'seller',
        shopId,
        statuses: SELLER_ACTION_STATUSES,
        page: 1,
        pageSize: 1,
      }),
    enabled: signedIn && mode === 'seller' && maySeeSellerOrders,
  });
  const stats = useQuery({
    queryKey: queryKeys.platform.stats,
    queryFn: api.platform.stats,
    enabled: signedIn && mode === 'admin',
  });
  const missions = useQuery({
    queryKey: queryKeys.inspections.list({}),
    queryFn: () => api.inspections.list({ page: 1, pageSize: 50 }),
    enabled: signedIn && mode === 'agent',
  });
  const unread = useQuery({
    queryKey: queryKeys.notifications.unread,
    queryFn: api.notifications.unreadCount,
    enabled: signedIn,
  });

  return {
    cart: cart.data?.itemCount ?? 0,
    messages:
      conversations.data?.items.reduce((total, item) => total + (item.unreadCount ?? 0), 0) ?? 0,
    sellerOrders: sellerOrders.data?.meta.totalItems ?? 0,
    adminModeration: stats.data
      ? stats.data.pendingReports +
        stats.data.pendingKyc +
        stats.data.pendingAgentApplications +
        stats.data.inspectionsToAssign +
        stats.data.inspectionsToValidate
      : 0,
    adminDisputes: stats.data?.openDisputes ?? 0,
    adminFinances: stats.data?.overdueInvoicesCount ?? 0,
    agentMissions:
      missions.data?.items.filter((item) => AGENT_ACTION_STATUSES.includes(item.status)).length ??
      0,
    notifications: unread.data?.count ?? 0,
  };
}
